import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calculator } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Checkbox } from "../ui/checkbox";
import { CALC, initState, calcTotal, suggestPack } from "./calc";

const SizeInput = ({ s, value, onChange }) => (
  <label className="flex flex-col gap-1 text-xs text-slate-400">
    {s.label}
    <input type="number" min={0} max={s.max} value={value} data-testid={`calc-size-${s.key}`}
      onChange={(e) => onChange(Math.max(0, Math.min(s.max, Number(e.target.value) || 0)))}
      className="w-32 bg-slate-950/70 border border-amber-500/25 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-amber-400" />
  </label>
);

const BalanceLine = ({ c, high, packs }) => {
  if (c.unlimited) return <p className="text-sm text-emerald-300" data-testid="calc-balance">Unlimited AI use on the Master Owner account — no credits needed.</p>;
  const need = Math.ceil(high - c.balance);
  const sug = suggestPack(need, packs);
  return (
    <div data-testid="calc-balance">
      <p className="text-sm text-slate-300">You have <span className="text-amber-200 font-mono">{Math.floor(c.balance).toLocaleString()}</span> credits.</p>
      {sug ? <p className="text-sm text-amber-200 mt-1" data-testid="calc-suggestion">About {need.toLocaleString()} more needed — {sug.count > 1 ? `${sug.count} × ` : "a "}{sug.pack.name} pack{sug.count > 1 ? "s" : ""} ({(sug.pack.credits * sug.count).toLocaleString()} credits, ${(sug.pack.price * sug.count).toFixed(0)}) {sug.count > 1 ? "cover" : "covers"} it. <Link to="/app/billing#packs" onClick={() => setTimeout(() => document.getElementById("packs")?.scrollIntoView({ behavior: "smooth" }), 50)} className="underline" data-testid="calc-buy-link">Top up</Link></p>
        : <p className="text-sm text-emerald-300 mt-1" data-testid="calc-suggestion">Your balance covers this project.</p>}
    </div>
  );
};

export const CostCalculator = ({ f, packs }) => {
  const { user } = useAuth();
  const [st, setSt] = useState(() => initState(f.id));
  const [c, setC] = useState(null);
  useEffect(() => { setSt(initState(f.id)); }, [f.id]);
  useEffect(() => {
    if (!user) return undefined;
    const load = () => api.get("/credits/me").then((r) => setC(r.data)).catch(() => {});
    load();
    window.addEventListener("focus", load);
    return () => window.removeEventListener("focus", load);
  }, [user, f.id]);
  const m = CALC[f.id];
  const mid = calcTotal(f.id, st, f.actions);
  const low = Math.round(mid * 0.7), high = Math.round(mid * 1.3);
  return (
    <div className="mt-6 bg-slate-900/80 border border-amber-500/20 rounded-xl p-6" data-testid="cost-calculator">
      <p className="eyebrow mb-5 flex items-center gap-2"><Calculator size={13} />Estimate your {f.name.toLowerCase()} project</p>
      <div className="flex flex-wrap gap-4 mb-5">{m.sizes.map((s) => <SizeInput key={s.key} s={s} value={st.sizes[s.key]} onChange={(v) => setSt((p) => ({ ...p, sizes: { ...p.sizes, [s.key]: v } }))} />)}</div>
      <div className="flex flex-wrap gap-x-6 gap-y-3 mb-6">
        {m.steps.map((s) => (
          <label key={s.key} className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
            <Checkbox checked={!!st.steps[s.key]} onCheckedChange={(v) => setSt((p) => ({ ...p, steps: { ...p.steps, [s.key]: !!v } }))} data-testid={`calc-step-${s.key}`} />{s.label}
          </label>
        ))}
      </div>
      <div className="flex flex-wrap items-end justify-between gap-4 border-t border-amber-500/15 pt-5">
        <p className="font-display text-3xl text-amber-100" data-testid="calc-total">{low.toLocaleString()}–{high.toLocaleString()} <span className="text-base text-slate-400">credits</span></p>
        {user && c ? <BalanceLine c={c} high={high} packs={packs} /> : !user && <p className="text-xs text-slate-500" data-testid="calc-signin-hint">Sign in to compare this with your credit balance.</p>}
      </div>
    </div>
  );
};
